import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types for walk observations
export interface WalkObservationData {
  // Original API format properties
  RSPW?: string; // Period Week
  RSISSUES?: number; // Issues count
  WalkStatus?: string; // Walk status
  RSUsername?: string; // Username (Finalized By)
  RSWALK_DATE?: string; // Walk date
  RSUID?: string; // Unique ID
  RSLOC?: string; // Location
  WALK_STARTED_BY?: string; // Who started the walk (Created By)
  LCISSUES?: number; // Load capacity issues count
  DAMAGEISSUES?: number; // Damage issues count
  TYPEOFDAMAGE?: string; // Type of damage

  // New API format properties (June 2025)
  UID?: string; // Unique ID in new format
  Status?: string; // Status in new format
  PW?: string; // Period Week in new format
  LOC?: string; // Location in new format
  "Total RS Issue"?: number; // Total Regulatory Standard Issues
  "Total LC Issues"?: number; // Total Load Capacity Issues
  "Walk Performed Date"?: string; // Walk date in new format
  "Walk Started BY"?: string; // Who started the walk in new format (Created By)
  "Last Modified By"?: string; // Who last modified the walk (Finalized By)
  CreatedBy?: string; // Alternative field for Created By
  FinalizedBy?: string; // Alternative field for Finalized By
}

// Interface for the state
export interface RackingWalkObservationsState {
  walkObservations: WalkObservationData[];
  loading: boolean;
  error: string | null;
  filteredWalkObservations: WalkObservationData[];
  filters: {
    period: string; // Separate period filter
    week: string; // Separate week filter
    year: string; // Added year filter
    periodWeek: string; // Keep for backward compatibility
    status: string;
    location: string;
    finalizedBy: string; // Added for "Finalized By" filter
    createdBy: string; // Added for "Created By" filter
    typeOfDamage: string; // Added for "Type of Damage" filter
  };
  filterOptions: Record<string, Array<{ id: number; displayText: string }>>;
  lastEmptyFetchKey?: string; // Track last filter/location combo that returned empty
}

// Initial state
const initialState: RackingWalkObservationsState = {
  walkObservations: [],
  loading: false,
  error: null,
  filteredWalkObservations: [],
  filters: {
    period: "",
    week: "",
    year: "",
    periodWeek: "",
    status: "",
    location: "",
    createdBy: "",
    finalizedBy: "",
    typeOfDamage: "",
  },
  filterOptions: {},
  lastEmptyFetchKey: undefined,
};

// Create the slice
const rackingWalkObservationsSlice = createSlice({
  name: "rackingWalkObservations",
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    // Set last empty fetch key
    setLastEmptyFetchKey: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.lastEmptyFetchKey = action.payload;
    },

    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      if (action.payload) {
        state.loading = false;
      }
    },

    // Set filter options from API
    setFilterOptions: (
      state,
      action: PayloadAction<
        Record<string, Array<{ id: number; displayText: string }>>
      >
    ) => {
      state.filterOptions = action.payload;
    },
    setWalkObservations: (
      state,
      action: PayloadAction<WalkObservationData[]>
    ) => {
      console.log("setWalkObservations action payload:", action.payload);
      console.log("Is payload array?", Array.isArray(action.payload));
      // Ensure we have an array
      const data = Array.isArray(action.payload) ? action.payload : [];
      state.walkObservations = data;
      
      // Apply current filters to the new data instead of showing all data
      // This prevents filter state from being lost when new data arrives
      state.filteredWalkObservations = data.filter((item) => {
        // Filter by period if specified
        if (state.filters.period && state.filters.period !== "") {
          // Extract period from period/week format (e.g., "P1W1" -> "P1")
          const periodWeek = item.RSPW || item.PW || "";
          const itemPeriod = periodWeek.split("W")[0]; // Extract "P1" from "P1W1"
          // Convert filter format "10" to period format "P10"
          const filterPeriod = state.filters.period.startsWith('P') ? state.filters.period : `P${state.filters.period}`;
          
          if (!itemPeriod || itemPeriod !== filterPeriod) {
            return false;
          }
        }
        // Filter by week if specified
        if (state.filters.week && state.filters.week !== "") {
          // Extract week from period/week format (e.g., "P1W1" -> "W1")
          const periodWeek = item.RSPW || item.PW || "";
          const itemWeek = periodWeek.includes("W")
            ? `W${periodWeek.split("W")[1]}`
            : "";
          // Convert filter format "1" to week format "W1"
          const filterWeek = state.filters.week.startsWith('W') ? state.filters.week : `W${state.filters.week}`;

          if (itemWeek !== filterWeek) {
            return false;
          }
        }
        // Keep the original periodWeek filter for backward compatibility
        if (state.filters.periodWeek && state.filters.periodWeek !== "") {
          // Check both old and new API formats
          const periodWeek = item.RSPW || item.PW;
          if (periodWeek !== state.filters.periodWeek) {
            return false;
          }
        }
        // Filter by status if specified
        if (state.filters.status && state.filters.status !== "") {
          // Check both old and new API formats
          const status = item.WalkStatus !== undefined ? item.WalkStatus : item.Status;
          const statusStr = String(status);
          if (statusStr !== state.filters.status) {
            return false;
          }
        }
        // Filter by location if specified
        if (state.filters.location && state.filters.location !== "") {
          // Check both old and new API formats
          const location = item.RSLOC || item.LOC;
          if (location !== state.filters.location) {
            return false;
          }
        }
        // Filter by finalized by if specified
        if (state.filters.finalizedBy && state.filters.finalizedBy !== "") {
          // Check both old and new API formats
          const finalizedBy =
            item.RSUsername || item["Last Modified By"] || item.FinalizedBy;
          if (!finalizedBy || finalizedBy !== state.filters.finalizedBy) {
            return false;
          }
        }
        // Filter by created by if specified
        if (state.filters.createdBy && state.filters.createdBy !== "") {
          // Check both old and new API formats
          const createdBy =
            item.WALK_STARTED_BY || item["Walk Started BY"] || item.CreatedBy;
          if (!createdBy || createdBy !== state.filters.createdBy) {
            return false;
          }
        }
        // Filter by type of damage if specified
        if (state.filters.typeOfDamage && state.filters.typeOfDamage !== "") {
          // Check both old and new API formats
          const typeOfDamage = item.TYPEOFDAMAGE || (item as any)["DM Damage Types"];
          if (!typeOfDamage || typeOfDamage !== state.filters.typeOfDamage) {
            return false;
          }
        }

        return true;
      });
      
      console.log(
        "State after update - walkObservations length:",
        state.walkObservations.length
      );
      console.log(
        "State after update - filteredWalkObservations length:",
        state.filteredWalkObservations.length
      );
    },
    // (removed duplicate setLastEmptyFetchKey)
    setFilters: (
      state,
      action: PayloadAction<{
        period?: string;
        week?: string;
        periodWeek?: string;
        status?: string;
        location?: string;
        finalizedBy?: string;
        createdBy?: string;
        typeOfDamage?: string;
      }>
    ) => {
      // Update the state with new filter values
      state.filters = { ...state.filters, ...action.payload };

      // Clean up empty filters - convert empty strings to actual empty values
      Object.keys(state.filters).forEach((key) => {
        if (state.filters[key as keyof typeof state.filters] === "") {
          state.filters[key as keyof typeof state.filters] = "";
        }
      });

      // Apply filters to the data
      state.filteredWalkObservations = state.walkObservations.filter((item) => {
        // Filter by period if specified
        if (state.filters.period && state.filters.period !== "") {
          // Extract period from period/week format (e.g., "P1W1" -> "P1")
          const periodWeek = item.RSPW || item.PW || "";
          const itemPeriod = periodWeek.split("W")[0]; // Extract "P1" from "P1W1"
          // Convert filter format "10" to period format "P10"
          const filterPeriod = state.filters.period.startsWith('P') ? state.filters.period : `P${state.filters.period}`;

          if (!itemPeriod || itemPeriod !== filterPeriod) {
            return false;
          }
        }
        // Filter by week if specified
        if (state.filters.week && state.filters.week !== "") {
          // Extract week from period/week format (e.g., "P1W1" -> "W1")
          const periodWeek = item.RSPW || item.PW || "";
          const itemWeek = periodWeek.includes("W")
            ? `W${periodWeek.split("W")[1]}`
            : "";
          // Convert filter format "1" to week format "W1"
          const filterWeek = state.filters.week.startsWith('W') ? state.filters.week : `W${state.filters.week}`;

          if (itemWeek !== filterWeek) {
            return false;
          }
        }
        // Keep the original periodWeek filter for backward compatibility
        if (state.filters.periodWeek && state.filters.periodWeek !== "") {
          // Check both old and new API formats
          const periodWeek = item.RSPW || item.PW;
          if (periodWeek !== state.filters.periodWeek) {
            return false;
          }
        }
        // Filter by status if specified
        if (state.filters.status && state.filters.status !== "") {
          // Check both old and new API formats
          const status = item.WalkStatus !== undefined ? item.WalkStatus : item.Status;
          const statusStr = String(status);
          if (statusStr !== state.filters.status) {
            return false;
          }
        }
        // Filter by location if specified
        if (state.filters.location && state.filters.location !== "") {
          // Check both old and new API formats
          const location = item.RSLOC || item.LOC;
          if (location !== state.filters.location) {
            return false;
          }
        } // Filter by finalized by if specified
        if (state.filters.finalizedBy && state.filters.finalizedBy !== "") {
          // Check both old and new API formats
          const finalizedBy =
            item.RSUsername || item["Last Modified By"] || item.FinalizedBy;
          if (!finalizedBy || finalizedBy !== state.filters.finalizedBy) {
            return false;
          }
        }
        // Filter by created by if specified
        if (state.filters.createdBy && state.filters.createdBy !== "") {
          // Check both old and new API formats
          const createdBy =
            item.WALK_STARTED_BY || item["Walk Started BY"] || item.CreatedBy;
          if (!createdBy || createdBy !== state.filters.createdBy) {
            return false;
          }
        }
        // Filter by type of damage if specified
        if (state.filters.typeOfDamage && state.filters.typeOfDamage !== "") {
          // Check both old and new API formats
          const typeOfDamage = item.TYPEOFDAMAGE || (item as any)["DM Damage Types"];
          if (!typeOfDamage || typeOfDamage !== state.filters.typeOfDamage) {
            return false;
          }
        }

        return true;
      });
    },

    // Reset filters
    resetFilters: (state) => {
      // Reset all filters to initial empty values
      state.filters = {
        period: "",
        week: "",
        year: "",
        periodWeek: "",
        status: "",
        location: "",
        finalizedBy: "",
        createdBy: "",
        typeOfDamage: "",
      };
      // Reset filtered data to show all walk observations
      state.filteredWalkObservations = state.walkObservations;
      console.log("Filters reset in Redux store");
    },
    // Set last empty fetch key must be inside reducers object
    // (removed duplicate setLastEmptyFetchKey)
  },
});

export const {
  setLoading,
  setError,
  setWalkObservations,
  setFilters,
  resetFilters,
  setFilterOptions,
  setLastEmptyFetchKey,
} = rackingWalkObservationsSlice.actions;

export default rackingWalkObservationsSlice.reducer;
